import { useCallback, useEffect, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { LockSimple, Trash, ArrowCounterClockwise, Eye, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Admin → Referrer Policy.
 *
 * Controls, per network, what the referring URL looks like when it is handed
 * upstream. `network` / `mode` / `key` are shown but locked: a macro key the
 * brand's tracking link does not contain, or a query-param typo (s3 → s4),
 * sends the network nothing and silently zeroes attribution — no error
 * anywhere, discovered only from a revenue report. Those need per-brand
 * validation before they can be edited safely.
 */
export default function AdminRefPolicies() {
  const [rows, setRows] = useState([]);
  const [opts, setOpts] = useState({ valid_formats: [], valid_fallbacks: [], valid_modes: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [urlDraft, setUrlDraft] = useState({});
  const [preview, setPreview] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(true);
  const [draft, setDraft] = useState({
    network: "", mode: "query_param", key: "",
    format: "url", fallback: "sub_id1", passthrough: false, ref_url: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/ref-policies", {
        params: showDeleted ? { include_deleted: 1 } : {},
      });
      setRows(data.policies || []);
      setOpts({
        valid_formats: data.valid_formats || [],
        valid_fallbacks: data.valid_fallbacks || [],
        valid_modes: data.valid_modes || [],
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load referrer policies");
    } finally {
      setLoading(false);
    }
  }, [showDeleted]);

  useEffect(() => { load(); }, [load]);

  const patch = async (network, changes) => {
    setSaving(network);
    try {
      // No auto_restore here: this is the inline-edit path and deleted rows
      // are listed by default, so sending it meant any ordinary field change
      // silently put a deleted policy back on live redirects. Bringing one
      // back is the Enabled toggle's job (toggleStatus -> POST /restore).
      await API.put(`/admin/ref-policies/${network}`, changes);
      toast.success(`Updated ${network}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving("");
    }
  };

  const toggleStatus = async (net, enable) => {
    try {
      if (enable) {
        await API.post(`/admin/ref-policies/${net}/restore`);
        toast.success(`${net} enabled`);
      } else {
        const { data } = await API.delete(`/admin/ref-policies/${net}`);
        if (data?.no_op) toast.warning(data.message || `${net}: nothing to remove`);
        else toast.success(`${net} disabled`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status update failed");
    }
  };

  const openPreview = async (net) => {
    try {
      const { data } = await API.get(`/admin/ref-policies/${net}/preview`);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const create = async () => {
    if (!draft.network.trim() || !draft.key.trim()) {
      toast.error("Network and key are required");
      return;
    }
    try {
      await API.put(`/admin/ref-policies/${draft.network.trim().toLowerCase()}`, draft);
      toast.success(`Added ${draft.network}`);
      setAddOpen(false);
      setDraft({
        network: "", mode: "query_param", key: "",
        format: "url", fallback: "sub_id1", passthrough: false, ref_url: "",
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  return (
    <div className="p-6" data-testid="admin-ref-policies">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-1">
        Tracking / Referrer
      </div>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Referrer Policy</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            What the referring URL looks like when it goes upstream. Every
            network is listed; greyed rows send no referrer at all — most
            don&apos;t need one, since they identify the publisher through their
            own subID slots. Only configure a network if it has told you it
            accepts a referrer.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            Show deactivated
          </label>
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-ref-policy">
            <Plus size={14} className="mr-1" /> Add network
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 mb-4 text-xs text-amber-900">
        <strong>Slot and key are locked.</strong> A macro key that is not present in
        the brand&apos;s tracking link, or a mistyped query param, sends the network
        nothing and silently zeroes attribution — there is no error to catch it.
        Ask an engineer to change those.
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 p-4">Loading…</div>
      ) : (
        <div className="border border-slate-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Network</th>
                <th className="text-left px-3 py-2">Format</th>
                <th className="text-left px-3 py-2">Fallback</th>
                <th className="text-left px-3 py-2">Referrer URL</th>
                <th className="text-left px-3 py-2">Real referrer</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const dead = r.status === "deleted" || r.status === "inactive";
                // A network with no policy sends no ref at all. Show it
                // greyed with a Configure action rather than hiding it —
                // otherwise an admin cannot tell it exists.
                const off = !r.configured || dead;
                return (
                  <tr key={r.network} className={off ? "opacity-60" : ""} data-testid={`ref-policy-${r.network}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">
                        {r.label || r.network}
                        {dead && <span className="ml-2 text-[9px] uppercase text-rose-600 font-bold">off</span>}
                      </div>
                      <div className="font-mono text-[10px] text-slate-400">
                        {r.network}
                        {r.configured
                          ? <> · {r.key} <LockSimple size={8} className="inline" /></>
                          : <span className="text-slate-400"> · no referrer sent</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Select value={r.format} onValueChange={(v) => patch(r.network, { format: v })} disabled={off || saving === r.network}>
                        <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {opts.valid_formats.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      <Select value={r.fallback} onValueChange={(v) => patch(r.network, { fallback: v })} disabled={off || saving === r.network}>
                        <SelectTrigger className="h-8 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {opts.valid_fallbacks.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        className="h-8 w-[230px] text-xs font-mono"
                        placeholder={r.fallback === "fixed" ? "https://site.com/page" : "(uses referrer / article)"}
                        value={urlDraft[r.network] ?? (r.ref_url || "")}
                        disabled={off || saving === r.network}
                        onChange={(e) => setUrlDraft({ ...urlDraft, [r.network]: e.target.value })}
                        onBlur={() => {
                          const next = (urlDraft[r.network] ?? "").trim();
                          if (urlDraft[r.network] === undefined || next === (r.ref_url || "")) return;
                          patch(r.network, { ref_url: next });
                          setUrlDraft((d) => { const c = { ...d }; delete c[r.network]; return c; });
                        }}
                        data-testid={`ref-url-${r.network}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <label className="flex items-center gap-2 text-xs" title="Bypass Traffic Masking so the browser sends a real Referer header">
                        <input
                          type="checkbox"
                          checked={!!r.passthrough}
                          disabled={off || saving === r.network}
                          onChange={(e) => patch(r.network, { passthrough: e.target.checked })}
                        />
                        pass through
                      </label>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {!r.configured ? (
                        <Button
                          size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => { setDraft({
                            network: r.network, mode: "query_param", key: "",
                            format: "url", fallback: "sub_id1",
                            passthrough: false, ref_url: "",
                          }); setAddOpen(true); }}
                          data-testid={`configure-${r.network}`}
                        >
                          <Plus size={13} className="mr-1" /> Configure
                        </Button>
                      ) : (
                        <div className="flex items-center justify-end gap-3">
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openPreview(r.network)}>
                            <Eye size={13} className="mr-1" /> Preview
                          </Button>
                          <div className="inline-flex items-center gap-2 text-xs bg-slate-100/90 px-2.5 py-1 rounded-md border border-slate-200" data-testid={`status-toggle-${r.network}`}>
                            <label className="inline-flex items-center gap-1 cursor-pointer font-medium text-emerald-700 select-none">
                              <input
                                type="radio"
                                name={`status-${r.network}`}
                                value="enable"
                                checked={!dead}
                                onChange={() => toggleStatus(r.network, true)}
                                disabled={saving === r.network}
                                className="accent-emerald-600 h-3 w-3 cursor-pointer"
                              />
                              Enable
                            </label>
                            <span className="text-slate-300">|</span>
                            <label className="inline-flex items-center gap-1 cursor-pointer font-medium text-slate-600 select-none">
                              <input
                                type="radio"
                                name={`status-${r.network}`}
                                value="disable"
                                checked={dead}
                                onChange={() => toggleStatus(r.network, false)}
                                disabled={saving === r.network}
                                className="accent-rose-600 h-3 w-3 cursor-pointer"
                              />
                              Disable
                            </label>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}



      {/* Preview — makes an otherwise invisible change concrete. */}
      <Dialog open={!!preview} onOpenChange={(v) => { if (!v) setPreview(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Preview — {preview?.network}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Sample referrer</div>
                <div className="font-mono text-xs break-all">{preview.sample_ref}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Sent upstream as</div>
                <div className="font-mono text-xs break-all text-emerald-700">{preview.rendered_ref}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">In the outbound URL</div>
                <div className="font-mono text-xs break-all">{preview.outbound_example}</div>
              </div>
              <p className="text-xs text-slate-500 border-t pt-2">{preview.note}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add network */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a network</DialogTitle>
            <DialogDescription>
              Slot and key are set once here and locked afterwards.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Network *</label>
              <Input className="mt-1 font-mono text-xs" placeholder="e.g. newnet"
                value={draft.network} onChange={(e) => setDraft({ ...draft, network: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Mode *</label>
                <Select value={draft.mode} onValueChange={(v) => setDraft({ ...draft, mode: v })}>
                  <SelectTrigger className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {opts.valid_modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Key *</label>
                <Input className="mt-1 font-mono text-xs" placeholder="s3 / sub3 / [REF]"
                  value={draft.key} onChange={(e) => setDraft({ ...draft, key: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Format</label>
                <Select value={draft.format} onValueChange={(v) => setDraft({ ...draft, format: v })}>
                  <SelectTrigger className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {opts.valid_formats.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Fallback</label>
                <Select value={draft.fallback} onValueChange={(v) => setDraft({ ...draft, fallback: v })}>
                  <SelectTrigger className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {opts.valid_fallbacks.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                Referrer URL {draft.fallback === "fixed" && <span className="text-rose-600">*</span>}
              </label>
              <Input className="mt-1 font-mono text-xs" placeholder="https://site.com/page"
                value={draft.ref_url} onChange={(e) => setDraft({ ...draft, ref_url: e.target.value })} />
              <p className="text-[11px] text-slate-400 mt-1">
                Sent when fallback is <span className="font-mono">fixed</span>, or when nothing else resolves.
              </p>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={draft.passthrough}
                onChange={(e) => setDraft({ ...draft, passthrough: e.target.checked })} />
              Send the real referrer (bypass Traffic Masking)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="create-ref-policy">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
